import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type INotification } from '@/types/INotification';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import {
  markRead,
  markAllRead,
  clearToasts,
  clearHistory,
  pruneStale,
  syncHistory
} from '@/store/notifications';

interface IUseNotificationCenter {
  isOpen: boolean;
  notifications: INotification[];
  unreadCount: number;
  hasUnread: boolean;
  toggle: () => void;
  close: () => void;
  handleSelect: (path: string, id: string) => void;
  handleMarkAllRead: () => void;
  handleClearAll: () => void;
}

/**
 * Логика центра уведомлений: открытие панели, выбор уведомления (переход +
 * пометка прочитанным), массовые действия. Бейдж считается от непросмотренных
 * заказов (`unviewedOrders`) — единый источник с фавиконом. При открытии
 * панели чистит протухшую историю (ленивая суточная чистка).
 */
export const useNotificationCenter = (): IUseNotificationCenter => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const notifications = useAppSelector((state) => state.notifications.history);
  const unreadCount = useAppSelector(
    (state) => state.unviewedOrders.uuids.length
  );

  const hasUnread = notifications.some((item) => !item.isRead);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    // dispatch только в обработчике события (не внутри setState-updater'а,
    // иначе обновление стора идёт во время рендера → React-варнинг).
    if (!isOpen) {
      dispatch(syncHistory());
      dispatch(pruneStale());
    }
    setIsOpen((prev) => !prev);
  }, [dispatch, isOpen]);

  const handleSelect = useCallback(
    (path: string, id: string) => {
      dispatch(markRead(id));
      void navigate(path);
      setIsOpen(false);
    },
    [dispatch, navigate]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead());
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(clearHistory());
    dispatch(clearToasts());
  }, [dispatch]);

  return {
    isOpen,
    notifications,
    unreadCount,
    hasUnread,
    toggle,
    close,
    handleSelect,
    handleMarkAllRead,
    handleClearAll
  };
};
